"use client";

import { c as _c } from "react/compiler-runtime";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import { motion } from "framer-motion";
import { useMemo, type FC } from "react";
import AdditionalMediaUpload from "../components/AdditionalMediaUpload";
import AirdropConfig from "../components/AirdropConfig";
import AllowlistBatchManager, { type AllowlistBatchRaw } from "../components/AllowlistBatchManager";
import PaymentConfig from "../components/PaymentConfig";
import { AIRDROP_TOTAL, type AirdropEntry, type PaymentInfo } from "../types/OperationalData";
import type { TraitsData } from "../types/TraitsData";
import { validateStrictAddress } from "../utils/addressValidation";
import { getSubmissionMetadataLengthValidation, type MetadataValueLengthStatus } from "../utils/submissionMetadata";
import { validateTokenIdFormat } from "../utils/tokenParsing";
interface AdditionalInfoStepProps {
  readonly traits: TraitsData;
  readonly airdropEntries: AirdropEntry[];
  readonly onAirdropEntriesChange: (entries: AirdropEntry[]) => void;
  readonly paymentInfo: PaymentInfo;
  readonly onPaymentInfoChange: (paymentInfo: PaymentInfo) => void;
  readonly allowlistBatches: AllowlistBatchRaw[];
  readonly supportingMedia: string[];
  readonly artworkCommentary: string;
  readonly aboutArtist: string;
  readonly previewImage: string;
  readonly promoVideo: string;
  readonly requiresPreviewImage: boolean;
  readonly requiresPromoVideoOption: boolean;
  readonly previewRequiredMediaType: string | null;
  readonly onBatchesChange: (batches: AllowlistBatchRaw[]) => void;
  readonly onSupportingMediaChange: (media: string[]) => void;
  readonly onPreviewImageChange: (url: string) => void;
  readonly onPromoVideoChange: (url: string) => void;
  readonly onArtworkCommentaryChange: (commentary: string) => void;
  readonly onAboutArtistChange: (aboutArtist: string) => void;
  readonly onBack: () => void;
  readonly onPreview: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting: boolean;
  readonly submitLabel?: string | undefined;
}
const AdditionalInfoStep: FC<AdditionalInfoStepProps> = t0 => {
  const $ = _c(93);
  const {
    traits,
    airdropEntries,
    onAirdropEntriesChange,
    paymentInfo,
    onPaymentInfoChange,
    allowlistBatches,
    supportingMedia,
    artworkCommentary,
    aboutArtist,
    previewImage,
    promoVideo,
    requiresPreviewImage,
    requiresPromoVideoOption,
    previewRequiredMediaType,
    onBatchesChange,
    onSupportingMediaChange,
    onPreviewImageChange,
    onPromoVideoChange,
    onArtworkCommentaryChange,
    onAboutArtistChange,
    onBack,
    onPreview,
    onSubmit,
    isSubmitting,
    submitLabel: t1
  } = t0;
  const submitLabel = t1 === undefined ? "Submit Artwork" : t1;
  let t2;
  if ($[0] !== aboutArtist || $[1] !== airdropEntries || $[2] !== allowlistBatches || $[3] !== artworkCommentary || $[4] !== paymentInfo || $[5] !== previewImage || $[6] !== promoVideo || $[7] !== supportingMedia || $[8] !== traits) {
    t2 = getSubmissionMetadataLengthValidation({
      traits,
      operationalData: {
        airdrop_config: airdropEntries,
        payment_info: paymentInfo,
        allowlist_batches: allowlistBatches,
        additional_media: {
          artist_profile_media: [],
          artwork_commentary_media: supportingMedia,
          preview_image: previewImage,
          promo_video: promoVideo
        },
        commentary: artworkCommentary,
        about_artist: aboutArtist
      }
    });
    $[0] = aboutArtist;
    $[1] = airdropEntries;
    $[2] = allowlistBatches;
    $[3] = artworkCommentary;
    $[4] = paymentInfo;
    $[5] = previewImage;
    $[6] = promoVideo;
    $[7] = supportingMedia;
    $[8] = traits;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const metadataLengthValidation = t2;
  const metadataStatuses = metadataLengthValidation.statusesByKey;
  const aboutArtistStatus = metadataStatuses.about_artist;
  const commentaryStatus = metadataStatuses.commentary;
  const metadataLengthError = _temp;
  let t3;
  if ($[10] !== aboutArtistStatus) {
    t3 = metadataLengthError(aboutArtistStatus);
    $[10] = aboutArtistStatus;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const aboutArtistError = t3;
  let t4;
  if ($[12] !== commentaryStatus) {
    t4 = metadataLengthError(commentaryStatus);
    $[12] = commentaryStatus;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const artworkCommentaryError = t4;
  const getContractError = _temp2;
  const getTokenIdsError = _temp3;
  let t5;
  if ($[14] !== aboutArtist || $[15] !== airdropEntries || $[16] !== allowlistBatches || $[17] !== artworkCommentary || $[18] !== metadataLengthValidation.hasErrors || $[19] !== paymentInfo || $[20] !== previewImage || $[21] !== requiresPreviewImage) {
    t5 = () => {
      const hasInvalidCount = airdropEntries.some(_temp4);
      if (hasInvalidCount) {
        return false;
      }
      const totalAllocated = airdropEntries.reduce(_temp5, 0);
      if (totalAllocated !== AIRDROP_TOTAL) {
        return false;
      }
      const hasEntryWithCountButNoValidAddress = airdropEntries.some(_temp6);
      if (hasEntryWithCountButNoValidAddress) {
        return false;
      }
      const hasEntryWithAddressButNoCount = airdropEntries.some(_temp7);
      if (hasEntryWithAddressButNoCount) {
        return false;
      }
      const hasInvalidAddress = airdropEntries.some(_temp8);
      if (hasInvalidAddress) {
        return false;
      }
      if (!paymentInfo.payment_address || !validateStrictAddress(paymentInfo.payment_address)) {
        return false;
      }
      const hasInvalidBatch = allowlistBatches.some(_temp9);
      if (hasInvalidBatch) {
        return false;
      }
      const hasInvalidTokenIds = allowlistBatches.some(_temp10);
      if (hasInvalidTokenIds) {
        return false;
      }
      if (!artworkCommentary.trim()) {
        return false;
      }
      if (!aboutArtist.trim()) {
        return false;
      }
      if (requiresPreviewImage && !previewImage) {
        return false;
      }
      if (metadataLengthValidation.hasErrors) {
        return false;
      }
      return true;
    };
    $[14] = aboutArtist;
    $[15] = airdropEntries;
    $[16] = allowlistBatches;
    $[17] = artworkCommentary;
    $[18] = metadataLengthValidation.hasErrors;
    $[19] = paymentInfo;
    $[20] = previewImage;
    $[21] = requiresPreviewImage;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const isFormValid = t5;
  const formValid = isFormValid();
  let t6;
  let t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 20
    };
    t7 = {
      opacity: 1,
      y: 0
    };
    t8 = {
      opacity: 0,
      y: -20
    };
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="tw-mb-8 tw-text-sm tw-text-iron-400">Complete the following details for distribution and storytelling purposes.</p>;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== airdropEntries || $[28] !== metadataStatuses.airdrop_config || $[29] !== onAirdropEntriesChange) {
    t10 = <AirdropConfig entries={airdropEntries} onEntriesChange={onAirdropEntriesChange} airdropLengthStatus={metadataStatuses.airdrop_config} />;
    $[27] = airdropEntries;
    $[28] = metadataStatuses.airdrop_config;
    $[29] = onAirdropEntriesChange;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== metadataStatuses.payment_info || $[32] !== onPaymentInfoChange || $[33] !== paymentInfo) {
    t11 = <PaymentConfig paymentInfo={paymentInfo} onPaymentInfoChange={onPaymentInfoChange} paymentInfoLengthStatus={metadataStatuses.payment_info} />;
    $[31] = metadataStatuses.payment_info;
    $[32] = onPaymentInfoChange;
    $[33] = paymentInfo;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const t12 = metadataStatuses.allowlist_batches;
  let t13;
  if ($[35] !== allowlistBatches) {
    let t14;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = batch_1 => {
        const contractError = getContractError(batch_1.contract);
        const tokenIdsError = getTokenIdsError(batch_1.token_ids_raw);
        return {
          ...(contractError ? {
            contract: contractError
          } : {}),
          ...(tokenIdsError ? {
            token_ids: tokenIdsError
          } : {})
        };
      };
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    t13 = allowlistBatches.map(t14);
    $[35] = allowlistBatches;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[38] !== allowlistBatches || $[39] !== metadataStatuses.allowlist_batches || $[40] !== onBatchesChange || $[41] !== t13) {
    t14 = <AllowlistBatchManager batches={allowlistBatches} onBatchesChange={onBatchesChange} allowlistLengthStatus={t12} errors={t13} />;
    $[38] = allowlistBatches;
    $[39] = metadataStatuses.allowlist_batches;
    $[40] = onBatchesChange;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const t15 = aboutArtistStatus?.isWarning && !aboutArtistStatus.isError ? aboutArtistStatus : undefined;
  const t16 = commentaryStatus?.isWarning && !commentaryStatus.isError ? commentaryStatus : undefined;
  let t17;
  if ($[43] !== aboutArtistError) {
    t17 = aboutArtistError ? {
      aboutArtist: aboutArtistError
    } : {};
    $[43] = aboutArtistError;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== artworkCommentaryError) {
    t18 = artworkCommentaryError ? {
      artworkCommentary: artworkCommentaryError
    } : {};
    $[45] = artworkCommentaryError;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t17 || $[48] !== t18) {
    t19 = {
      ...t17,
      ...t18
    };
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== aboutArtist || $[51] !== artworkCommentary || $[52] !== metadataStatuses.additional_media || $[53] !== onAboutArtistChange || $[54] !== onArtworkCommentaryChange || $[55] !== onPreviewImageChange || $[56] !== onPromoVideoChange || $[57] !== onSupportingMediaChange || $[58] !== previewImage || $[59] !== previewRequiredMediaType || $[60] !== promoVideo || $[61] !== requiresPreviewImage || $[62] !== requiresPromoVideoOption || $[63] !== supportingMedia || $[64] !== t15 || $[65] !== t16 || $[66] !== t19) {
    t20 = <AdditionalMediaUpload supportingMedia={supportingMedia} artworkCommentary={artworkCommentary} aboutArtist={aboutArtist} previewImage={previewImage} promoVideo={promoVideo} requiresPreviewImage={requiresPreviewImage} requiresPromoVideoOption={requiresPromoVideoOption} previewRequiredMediaType={previewRequiredMediaType} onSupportingMediaChange={onSupportingMediaChange} onPreviewImageChange={onPreviewImageChange} onPromoVideoChange={onPromoVideoChange} onArtworkCommentaryChange={onArtworkCommentaryChange} onAboutArtistChange={onAboutArtistChange} aboutArtistLengthStatus={t15} artworkCommentaryLengthStatus={t16} additionalMediaLengthStatus={metadataStatuses.additional_media} errors={t19} />;
    $[50] = aboutArtist;
    $[51] = artworkCommentary;
    $[52] = metadataStatuses.additional_media;
    $[53] = onAboutArtistChange;
    $[54] = onArtworkCommentaryChange;
    $[55] = onPreviewImageChange;
    $[56] = onPromoVideoChange;
    $[57] = onSupportingMediaChange;
    $[58] = previewImage;
    $[59] = previewRequiredMediaType;
    $[60] = promoVideo;
    $[61] = requiresPreviewImage;
    $[62] = requiresPromoVideoOption;
    $[63] = supportingMedia;
    $[64] = t15;
    $[65] = t16;
    $[66] = t19;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== t10 || $[69] !== t11 || $[70] !== t14 || $[71] !== t20) {
    t21 = <div className="tw-mx-auto tw-w-full tw-max-w-4xl tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-8 tw-pt-8 tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-700 md:tw-px-8">{t9}<div className="tw-flex tw-flex-col tw-gap-y-10">{t10}{t11}{t14}{t20}</div></div>;
    $[68] = t10;
    $[69] = t11;
    $[70] = t14;
    $[71] = t20;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== isSubmitting || $[74] !== onBack) {
    t22 = <SecondaryButton onClicked={onBack} disabled={isSubmitting}>Back</SecondaryButton>;
    $[73] = isSubmitting;
    $[74] = onBack;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  const t23 = !formValid || isSubmitting;
  let t24;
  if ($[76] !== onPreview || $[77] !== t23) {
    t24 = <SecondaryButton onClicked={onPreview} disabled={t23}>Preview</SecondaryButton>;
    $[76] = onPreview;
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  const t25 = !formValid;
  let t26;
  if ($[79] !== isSubmitting || $[80] !== onSubmit || $[81] !== submitLabel || $[82] !== t25) {
    t26 = <PrimaryButton onClicked={onSubmit} disabled={t25} loading={isSubmitting} padding="tw-px-6 tw-py-3">{submitLabel}</PrimaryButton>;
    $[79] = isSubmitting;
    $[80] = onSubmit;
    $[81] = submitLabel;
    $[82] = t25;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== t24 || $[85] !== t26) {
    t27 = <div className="tw-flex tw-items-center tw-gap-2">{t24}{t26}</div>;
    $[84] = t24;
    $[85] = t26;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== t22 || $[88] !== t27) {
    t28 = <div className="tw-mt-auto tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-py-3"><div className="tw-mx-auto tw-flex tw-w-full tw-max-w-4xl tw-items-center tw-justify-between tw-px-4 md:tw-px-8">{t22}{t27}</div></div>;
    $[87] = t22;
    $[88] = t27;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== t21 || $[91] !== t28) {
    t29 = <motion.div initial={t6} animate={t7} exit={t8} className="tw-flex tw-h-full tw-w-full tw-flex-col">{t21}{t28}</motion.div>;
    $[90] = t21;
    $[91] = t28;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  return t29;
};
export default AdditionalInfoStep;
function _temp(status) {
  if (!status?.isError) {
    return;
  }
  return `${status.length}/${status.maxLength} characters exceed the limit`;
}
function _temp2(address) {
  if (!address) {
    return;
  }
  if (!validateStrictAddress(address)) {
    return "Invalid contract address";
  }
}
function _temp3(tokenIds) {
  return validateTokenIdFormat(tokenIds) ?? undefined;
}
function _temp4(e) {
  return !Number.isFinite(e.count) || e.count < 0 || !Number.isInteger(e.count);
}
function _temp5(sum, e_0) {
  return sum + e_0.count;
}
function _temp6(e_1) {
  return e_1.count > 0 && (!e_1.address || !validateStrictAddress(e_1.address));
}
function _temp7(e_2) {
  return e_2.address.length > 0 && validateStrictAddress(e_2.address) && e_2.count <= 0;
}
function _temp8(e_3) {
  return e_3.address.length > 0 && !validateStrictAddress(e_3.address);
}
function _temp9(batch) {
  return !batch.contract || !validateStrictAddress(batch.contract);
}
function _temp10(batch_0) {
  return validateTokenIdFormat(batch_0.token_ids_raw) !== null;
}